import defaultValues, { OnboardingUserValues } from './values';
import transformRules from './transform-rules';
import schemas, { Schemas } from './schemas';
import schemasLite from './schemas-lite';
import getSummary, { Summary, SectionSummaryItem } from './summary';
import { ValidatedSections, ValidateSectionOptions } from './validation';
import * as lookups from './lookups';

type OnboardingUserAttributes = {
    onboardingId: string;
    prescreenResult: string;
    onboardingStatus: string;
    userId: string;
    createdAt: string;
    updatedAt?: string;
    approvedAt?: string;
    rejectedAt?: string;
    completedAt?: string;
    approvedBy?: string;
    rejectedBy?: string;
    onboardingExistingUser?: boolean;
    campaign?: string;
    onboardingLite?: boolean;
};

type OnboardingUserModel = {
    values: OnboardingUserValues;
    attributes: OnboardingUserAttributes;
};

type ErrorType = Error & {
    errors: string[];
};

const defaultAttributes = {
    onboardingId: '',
    prescreenResult: '',
    onboardingStatus: '',
    userId: '',
    createdAt: '',
    updatedAt: '',
    approvedAt: '',
    rejectedAt: '',
    completedAt: '',
    approvedBy: '',
    rejectedBy: '',
    onboardingExistingUser: false,
    campaign: '',
    onboardingLite: false,
};

export class OnboardingUser implements OnboardingUserModel {
    // static schemas = schemas;
    // static schemasLite = schemasLite;
    static lookups = lookups;
    private _values = defaultValues;
    private _attributes = defaultAttributes;

    get values(): OnboardingUserValues {
        return this._values;
    }

    set values(values: OnboardingUserValues) {
        throw new Error('Values cannot be set directly, please use the update method.');
    }

    get attributes(): OnboardingUserAttributes {
        return this._attributes;
    }

    set attributes(attributes: OnboardingUserAttributes) {
        throw new Error('Attributes cannot be modified.');
    }

    get schemas(): Schemas {
        return this._attributes.onboardingLite ? schemasLite : schemas;
    }

    update(values: Partial<OnboardingUserValues>): this {
        this._values = {
            ...this._values,
            ...values,
        };

        return this;
    }

    import(values: Record<string, unknown>): this {
        const transformedValues = Object.entries(values).reduce((result, [key, value]) => {
            if (key in this._attributes) {
                return result;
            }

            let output = value;
            const transformFn = transformRules[key]?.load;

            if (key in transformRules && transformFn) {
                output = transformFn(value);
            }

            return {
                ...result,
                [key]: output,
            };
        }, <OnboardingUserValues>{});

        const attributes = Object.entries(values).reduce((result, [key, value]) => {
            if (!(key in this._attributes)) {
                return result;
            }

            return {
                ...result,
                [key]: value,
            };
        }, <OnboardingUserAttributes>{});

        this._values = {
            ...this._values,
            ...transformedValues,
        };

        this._attributes = {
            ...this._attributes,
            ...attributes,
        };

        return this;
    }

    export(): Record<string, unknown> {
        const transformedValues = Object.entries(this._values).reduce((result, [key, value]) => {
            let output = value;
            const transformFn = transformRules[key]?.save;

            if (key in transformRules && transformFn) {
                output = transformFn(value);
            }

            return {
                ...result,
                [key]: output,
            };
        }, <Record<string, unknown>>{});

        return {
            ...this._values,
            ...transformedValues,
        };
    }

    async validateSections(options: ValidateSectionOptions = {}): Promise<ValidatedSections> {
        const validatedSections = await Promise.all(
            Object.entries(this.schemas).map(async ([id, schema]) => {
                let requiredFieldCount = 0;
                let errorCount = 0;
                let errors: string[] = [];

                try {
                    await schema.validate(defaultValues, { abortEarly: false });
                } catch (err) {
                    requiredFieldCount = (err as ErrorType).errors.length;
                }

                const valuesToValidate = { ...this._values };

                if (options.acceptRedactedFinancialDetails) {
                    valuesToValidate.accountNumber = '00000000';
                    valuesToValidate.sortCode = '00-00-00';
                }

                try {
                    await schema.validate(valuesToValidate, { abortEarly: false });
                } catch (err) {
                    const error = err as ErrorType;
                    if (!error.errors) {
                        console.error(err);
                    }
                    errorCount = error.errors.length;
                    errors = error.errors;
                }

                if (errorCount > requiredFieldCount) {
                    errorCount = requiredFieldCount;
                }

                return {
                    id,
                    errors,
                    isValid: errorCount === 0,
                    isOptional: requiredFieldCount === 0,
                    fractionComplete: errorCount === 0 ? 1 : 1 - errorCount / requiredFieldCount,
                };
            }),
        );

        return validatedSections.reduce(
            (result, { id, isValid, isOptional, fractionComplete, errors }) => ({
                ...result,
                [id]: {
                    isValid,
                    errors,
                    isOptional,
                    fractionComplete,
                },
            }),
            <ValidatedSections>{},
        );
    }

    async summary(): Promise<Summary> {
        const validatedSections = await this.validateSections({
            acceptRedactedFinancialDetails: true,
        });

        const summary: Summary = getSummary();

        Object.keys(summary).forEach((key) => {
            summary[key].validation = validatedSections[key];

            const fields = Object.keys(summary[key].items) as Array<keyof OnboardingUserValues>;

            fields.forEach((field) => {
                const item = summary[key].items[field] as SectionSummaryItem;
                let value = this.values[field];
                if (value === 'yes' || value === true) {
                    value = 'Yes';
                }
                if (value === 'no') {
                    value = 'No';
                }
                if (Array.isArray(value)) {
                    value = value.join(', ');
                }
                item.value = value;
            });
        });

        return summary;
    }
}
